import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import { IStoreState } from "../initialStoreState";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { ICatalog } from "./Catalogs.types";
import { parseMaybeJson } from "../../helpers/parseMaybeJson";

// Action type constants
export const FETCH_CATALOG_LIST_PROGRESS = "FETCH_CATALOG_LIST_PROGRESS";
export const FETCH_CATALOG_LIST_SUCCESS = "FETCH_CATALOG_LIST_SUCCESS";
export const FETCH_CATALOG_LIST_FAILED = "FETCH_CATALOG_LIST_FAILED";
export const CLEAR_CATALOGS_STATE = "CLEAR_CATALOGS_STATE";

// Action creators
export const fetchCatalogListProgress = () =>
  action(FETCH_CATALOG_LIST_PROGRESS);
export const fetchCatalogListSuccess = (
  data: ICatalog[],
  totalRecords: number,
) => action(FETCH_CATALOG_LIST_SUCCESS, { data, totalRecords });
export const fetchCatalogListFailed = () => action(FETCH_CATALOG_LIST_FAILED);
export const clearCatalogsState = () => action(CLEAR_CATALOGS_STATE);

// Async thunks
export const fetchCatalogListAsync =
  (
    companyOutletUuid: string,
    pageNo: number = 1,
    itemPerPage: number = 20,
    status: string = "ACTIVE",
    searchValue?: string,
    fromDate?: string,
    toDate?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(fetchCatalogListProgress());
    dispatch(saveLoaderProgress());
    try {
      let url = `/genai/get-internal-catalog?company_outlet_uuid=${companyOutletUuid}&status=${status}&pageNo=${pageNo}&itemPerPage=${itemPerPage}`;

      if (searchValue) {
        url += `&columns=${JSON.stringify(["catalog_name", "description", "catalog_type"])}&value=${encodeURIComponent(searchValue)}`;
      }
      if (fromDate) {
        url += `&from_date=${fromDate}`;
      }
      if (toDate) {
        url += `&to_date=${toDate}`;
      }

      const response = await api.get(url);
      const rawData: ICatalog[] = response.data.data || [];
      const data = rawData.map((catalog) => ({
        ...catalog,
        file_path: parseMaybeJson<string[]>(catalog.file_path as any, []),
      }));
      const totalRecords = response.data.totalRecords || 0;
      dispatch(fetchCatalogListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchCatalogListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch catalogs",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const uploadCatalogFilesAsync =
  (
    files: File[],
    companyOutletUuid: string,
    onCallback: (isSuccess: boolean, data?: string[]) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });
      formData.append("module_name", "WHATSAPP_SEND_ATTACHMENTS");
      formData.append(
        "as_payload",
        JSON.stringify({
          phone_number: `catalogs_${companyOutletUuid}`,
        }),
      );

      const response = await api.post("/general/upload-files", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const uploadedPaths: string[] = response.data.data || [];
      onCallback(true, uploadedPaths);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to upload files",
          displayAs: "snackbar",
        }),
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertCatalogAsync =
  (
    data: ICatalog,
    onCallback: (isSuccess: boolean, data?: ICatalog) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const response = await api.post("/genai/upsert-internal-catalog", data);
      dispatch(
        showMessage({
          type: "success",
          message: response.data.message || "Catalog saved successfully",
          displayAs: "snackbar",
        }),
      );
      onCallback(true, response.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to save catalog",
          displayAs: "snackbar",
        }),
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const deleteCatalogAsync =
  (
    internalCatalogsUuid: string,
    companyOutletUuid: string,
    onCallback: (success: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const response = await api.get(
        `/genai/delete-meta-catalog?internal_catalogs_uuid=${internalCatalogsUuid}&company_outlet_uuid=${companyOutletUuid}`,
      );
      dispatch(
        showMessage({
          type: "success",
          message: response.data.message || "Catalog deleted successfully",
          displayAs: "snackbar",
        }),
      );
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to delete catalog",
          displayAs: "snackbar",
        }),
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
